import { useEffect, useRef, useState } from 'react';
import { Text, Badge, Spinner } from '@shopify/polaris';
import { formatToCurrency } from '../../../../shared/components/functions.jsx';
import { getLineLabel, getUnitLabel, getAreasSorted, AREAS } from '../../../../config/roasterySpec';
import LineTechDescEditor from './LineTechDescEditor.jsx';

function parseTechDescImages(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    return [];
  }
}

function extractTextFromNode(node) {
  if (!node || typeof node !== 'object') return '';
  if (node.type === 'text' && typeof node.text === 'string') return node.text;
  if (!Array.isArray(node.content)) return '';
  return node.content.map(extractTextFromNode).join(' ');
}

function extractTechDescSnippet(value) {
  if (!value) return '';
  if (typeof value !== 'string') return '';

  try {
    const parsed = JSON.parse(value);
    const text = extractTextFromNode(parsed).replace(/\s+/g, ' ').trim();
    if (!text) return '';
    return text.length > 90 ? `${text.slice(0, 90)}...` : text;
  } catch (err) {
    const text = value.replace(/\s+/g, ' ').trim();
    return text.length > 90 ? `${text.slice(0, 90)}...` : text;
  }
}

function normalizeResolvedLabel(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function getLineDescription(line) {
  return String(line?.Description || line?.Product?.ProductNameNL || line?.Product?.ProductName || '').trim();
}

function buildTechDescDeleteWarning(line) {
  const hasEn = !!line.TechnicalDescription;
  const hasNl = !!line.TechnicalDescriptionNL;
  const hasFr = !!line.TechnicalDescriptionFR;
  const images = parseTechDescImages(line.TechDescImages);
  const hasTechContent = hasEn || hasNl || hasFr || images.length > 0;

  const context = `${line.AreaCode || '-'} > ${String(line.LineNumber || 0).padStart(3, '0')} > ${String(line.UnitNumber || 0).padStart(4, '0')}`;
  const product = `${line.Product?.Productref || '-'} ${line.Description || line.Product?.ProductNameNL || 'Unnamed item'}`.trim();

  if (!hasTechContent) {
    return `Remove this line?\n\nItem: ${product}\nLocation: ${context}`;
  }

  const details = [];
  const enSnippet = extractTechDescSnippet(line.TechnicalDescription);
  const nlSnippet = extractTechDescSnippet(line.TechnicalDescriptionNL);
  const frSnippet = extractTechDescSnippet(line.TechnicalDescriptionFR);
  if (enSnippet) details.push(`EN: "${enSnippet}"`);
  if (nlSnippet) details.push(`NL: "${nlSnippet}"`);
  if (frSnippet) details.push(`FR: "${frSnippet}"`);
  if (images.length > 0) details.push(`Images: ${images.length}`);

  return `Remove this line?\n\nItem: ${product}\nLocation: ${context}\n\nThis will also remove technical description content:\n${details.join('\n')}\n\nContinue?`;
}

// Flatten nested BOM tree into rows with accumulated quantities
function flattenBomTree(children, parentQty, level) {
  const rows = [];
  for (const node of children) {
    const accQty = node.quantity * parentQty;
    rows.push({
      productId: node.productId,
      name: node.name,
      ref: node.ref,
      unitCost: node.unitCost,
      quantity: accQty,
      localQty: node.quantity,
      isLeaf: node.isLeaf,
      level
    });
    if (node.children && node.children.length > 0) {
      rows.push(...flattenBomTree(node.children, accQty, level + 1));
    }
  }
  return rows;
}

function marginColorFor(marginPct) {
  return marginPct >= 30 ? '#27ae60' : marginPct >= 15 ? '#f39c12' : '#e74c3c';
}

// Accepts "1234.56", "1234,56", "€ 12", "5%" — returns a number or null
function parseNumericInput(raw) {
  if (raw == null) return null;
  const cleaned = String(raw)
    .replace(/[€%\s]/g, '')
    .replace(',', '.');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
}

function roundTwo(value) {
  return Math.round(value * 100) / 100;
}

// A single spreadsheet cell: shows the formatted value, becomes a raw editor on
// focus, commits on blur (only when changed). Enter/arrow keys hand navigation
// back to the table; Alt+arrows move the whole line.
function CellInput({ display, editValue, onCommit, onNavigate, onMoveLine, align = 'right', inputStyle, disabled, cellKey, title, placeholder }) {
  const [draft, setDraft] = useState(null);
  const inputRef = useRef(null);
  const selectOnRenderRef = useRef(false);

  // Select-all must run after the focus re-render: swapping the displayed value
  // for the raw edit value rewrites the input and would collapse the selection
  useEffect(() => {
    if (selectOnRenderRef.current && inputRef.current) {
      selectOnRenderRef.current = false;
      inputRef.current.select();
    }
  });

  if (disabled) {
    return (
      <span className={`qlt-cell-static${align === 'right' ? ' qlt-cell-static-numeric' : ''}`} style={inputStyle} title={title}>
        {display}
      </span>
    );
  }

  const editString = String(editValue ?? '');

  return (
    <input
      ref={inputRef}
      className={`qlt-cell-input${align === 'right' ? ' qlt-cell-input-numeric' : ''}`}
      style={inputStyle}
      value={draft ?? display ?? ''}
      title={title}
      placeholder={placeholder}
      data-cell={cellKey}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => {
        setDraft(editString);
        selectOnRenderRef.current = true;
      }}
      onBlur={() => {
        if (draft != null && draft !== editString) onCommit(draft);
        setDraft(null);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (!onNavigate?.(e.shiftKey ? -1 : 1)) e.currentTarget.blur();
        } else if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && e.altKey) {
          e.preventDefault();
          onMoveLine?.(e.key === 'ArrowDown' ? 1 : -1);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          onNavigate?.(1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          onNavigate?.(-1);
        } else if (e.key === 'Escape') {
          setDraft(null);
        }
      }}
    />
  );
}

const COLUMN_COUNT = 10;

export default function QuotationLineTable({ lines, quotationId, onRefresh, onLineUpdate, onLineRemove, onConfigsRefresh, onAddToGroup, onReplaceProduct, isLocked, areaConfigs }) {
  const [expandedBomLines, setExpandedBomLines] = useState(new Set());
  const [bomTrees, setBomTrees] = useState({});
  const [bomLoading, setBomLoading] = useState(new Set());
  const [expandedTechDesc, setExpandedTechDesc] = useState(new Set());
  const [pendingSaves, setPendingSaves] = useState(0);
  const [hasSaved, setHasSaved] = useState(false);
  const wrapRef = useRef(null);

  const toggleTechDesc = (lineId) => {
    setExpandedTechDesc((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const applyLinePatch = (patch) => {
    if (onLineUpdate) onLineUpdate(patch);
    else onRefresh();
  };

  const saveLine = async (line, updates) => {
    applyLinePatch({ ID: line.ID, ...updates });
    setPendingSaves((c) => c + 1);
    try {
      const res = await fetch(`/api/project-quotations/${quotationId}/lines/${line.ID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        applyLinePatch(await res.json());
        setHasSaved(true);
      } else {
        window.alert('Saving the line failed. Reloading data.');
        onRefresh();
      }
    } catch (err) {
      window.alert('Saving the line failed. Reloading data.');
      onRefresh();
    } finally {
      setPendingSaves((c) => c - 1);
    }
  };

  // Commit one edited cell: parses the raw input, derives the field update and
  // skips the request when nothing actually changed
  const commitField = (line, field, raw) => {
    if (field === 'desc') {
      const next = raw.trim();
      if (next === String(line.Description || '')) return;
      saveLine(line, { Description: next });
      return;
    }

    const num = parseNumericInput(raw);
    if (num == null) return;

    const qty = parseFloat(line.Quantity) || 0;
    const discountFactor = 1 - (parseFloat(line.DiscountPercent) || 0) / 100;

    if (field === 'unit') {
      const unitNumber = Math.trunc(num);
      if (unitNumber < 0 || unitNumber === (line.UnitNumber || 0)) return;
      saveLine(line, { UnitNumber: unitNumber });
    } else if (field === 'qty') {
      if (num === (parseFloat(line.Quantity) || 0)) return;
      saveLine(line, { Quantity: num });
    } else if (field === 'ap') {
      if (num === (parseFloat(line.UnitPurchasePrice) || 0)) return;
      saveLine(line, { UnitPurchasePrice: num });
    } else if (field === 'vp') {
      if (num === (parseFloat(line.UnitSellingPrice) || 0)) return;
      saveLine(line, { UnitSellingPrice: num });
    } else if (field === 'disc') {
      const disc = Math.min(100, Math.max(0, num));
      if (disc === (parseFloat(line.DiscountPercent) || 0)) return;
      saveLine(line, { DiscountPercent: disc });
    } else if (field === 'total') {
      // Typing a target line total back-computes the unit selling price
      if (qty <= 0 || discountFactor <= 0) return;
      const vp = roundTwo(num / (qty * discountFactor));
      if (vp === (parseFloat(line.UnitSellingPrice) || 0)) return;
      saveLine(line, { UnitSellingPrice: vp });
    } else if (field === 'margin') {
      // Typing a target margin % back-computes the unit selling price from AP
      const ap = parseFloat(line.UnitPurchasePrice) || 0;
      const margin = Math.min(99.9, num);
      if (ap <= 0) return;
      const vp = roundTwo(ap / (1 - margin / 100));
      if (vp === (parseFloat(line.UnitSellingPrice) || 0)) return;
      saveLine(line, { UnitSellingPrice: vp });
    }
  };

  const handleDelete = async (line) => {
    const confirmMessage = buildTechDescDeleteWarning(line);
    if (!window.confirm(confirmMessage)) return;
    const res = await fetch(`/api/project-quotations/${quotationId}/lines/${line.ID}`, { method: 'DELETE' });
    if (res.ok && onLineRemove) onLineRemove(line.ID);
    else onRefresh();
  };

  const handleDuplicate = async (line) => {
    setPendingSaves((c) => c + 1);
    try {
      const res = await fetch(`/api/project-quotations/${quotationId}/lines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          AreaCode: line.AreaCode,
          LineNumber: line.LineNumber,
          UnitNumber: line.UnitNumber || 0,
          ProductID: line.ProductID,
          Description: line.Description,
          Quantity: line.Quantity,
          UnitPurchasePrice: line.UnitPurchasePrice,
          UnitSellingPrice: line.UnitSellingPrice,
          IsOptional: line.IsOptional,
          ShowBomBreakdown: line.ShowBomBreakdown,
          DiscountPercent: line.DiscountPercent,
          Notes: line.Notes
        })
      });
      if (res.ok) {
        applyLinePatch(await res.json());
        setHasSaved(true);
      } else {
        window.alert('Duplicating the line failed.');
        onRefresh();
      }
    } finally {
      setPendingSaves((c) => c - 1);
    }
  };

  const siblingsOf = (line) => {
    const unitNum = line.UnitNumber || 0;
    return lines
      .filter((l) => l.AreaCode === line.AreaCode && l.LineNumber === line.LineNumber && (l.UnitNumber || 0) === unitNum)
      .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0) || a.ID - b.ID);
  };

  // Moves a line one visual slot up/down. Within its (area, line, unit) group
  // this swaps sort order; at the group edge the line hops into the neighboring
  // group, adopting that group's area/line/unit.
  const moveLine = async (line, direction) => {
    const flatIdx = sorted.findIndex((l) => l.ID === line.ID);
    const neighbor = sorted[flatIdx + direction];
    if (flatIdx < 0 || !neighbor) return;

    const sameGroup = neighbor.AreaCode === line.AreaCode && neighbor.LineNumber === line.LineNumber && (neighbor.UnitNumber || 0) === (line.UnitNumber || 0);

    if (sameGroup) {
      const siblings = siblingsOf(line);
      const idx = siblings.findIndex((l) => l.ID === line.ID);
      const target = idx + direction;
      if (idx < 0 || target < 0 || target >= siblings.length) return;

      const reordered = [...siblings];
      [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
      const lineOrders = reordered.map((l, i) => ({ lineId: l.ID, sortOrder: i + 1 }));

      for (const item of lineOrders) applyLinePatch({ ID: item.lineId, SortOrder: item.sortOrder });

      const res = await fetch(`/api/project-quotations/${quotationId}/lines/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lineOrders })
      });
      if (!res.ok) {
        window.alert('Reordering failed. Reloading data.');
        onRefresh();
      }
      return;
    }

    // Cross-group hop: join the neighbor's group, landing at the end of it when
    // moving up and at the start when moving down (the visually adjacent slot)
    const targetSiblings = siblingsOf(neighbor);
    const groupPatch = { AreaCode: neighbor.AreaCode, LineNumber: neighbor.LineNumber, UnitNumber: neighbor.UnitNumber || 0 };
    const newOrder = direction < 0 ? [...targetSiblings, line] : [line, ...targetSiblings];
    const lineOrders = newOrder.map((l, i) => ({ lineId: l.ID, sortOrder: i + 1 }));
    const movedOrder = lineOrders.find((o) => o.lineId === line.ID);

    applyLinePatch({ ID: line.ID, ...groupPatch });
    for (const item of lineOrders) applyLinePatch({ ID: item.lineId, SortOrder: item.sortOrder });

    const moveRes = await fetch(`/api/project-quotations/${quotationId}/lines/${line.ID}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...groupPatch, SortOrder: movedOrder.sortOrder })
    });
    const reorderRes = await fetch(`/api/project-quotations/${quotationId}/lines/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lineOrders: lineOrders.filter((o) => o.lineId !== line.ID) })
    });
    if (!moveRes.ok || !reorderRes.ok) {
      window.alert('Moving the line failed. Reloading data.');
      onRefresh();
    }
  };

  const putAreaConfigs = async (configs) => {
    setPendingSaves((c) => c + 1);
    try {
      const res = await fetch(`/api/project-quotations/${quotationId}/area-configs`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ configs })
      });
      if (res.ok) setHasSaved(true);
      else window.alert('Saving failed. Reloading data.');
      if (onConfigsRefresh) onConfigsRefresh();
    } finally {
      setPendingSaves((c) => c - 1);
    }
  };

  // Rename a group header (area / line / unit custom label)
  const saveGroupLabel = (row, raw) => {
    const next = raw.trim();
    if (next === String(row.label || '')) return;
    putAreaConfigs([{ AreaCode: row.areaCode, LineNumber: row.lineNumber ?? null, UnitNumber: row.unitNumber ?? null, CustomLabel: next || null }]);
  };

  // Swap an area with its nearest visible neighbour, renumbering the full
  // effective area order (including empty areas) so it stays unambiguous
  const moveArea = (areaCode, direction, visibleAreas) => {
    const all = getAreasSorted().map((a) => a.code);
    for (const l of lines) if (!all.includes(l.AreaCode)) all.push(l.AreaCode);
    const effective = [...all].sort((a, b) => areaOrder(a) - areaOrder(b));

    const vIdx = visibleAreas.indexOf(areaCode);
    const neighbor = visibleAreas[vIdx + direction];
    if (vIdx < 0 || !neighbor) return;

    const withoutMoved = effective.filter((c) => c !== areaCode);
    const nIdx = withoutMoved.indexOf(neighbor);
    withoutMoved.splice(direction < 0 ? nIdx : nIdx + 1, 0, areaCode);

    putAreaConfigs(withoutMoved.map((code, i) => ({ AreaCode: code, LineNumber: null, UnitNumber: null, SortOrder: i + 1 })));
  };

  // Remove every item in an area or line group
  const handleRemoveScope = async (row) => {
    const isLineScope = row.level === 'line';
    const scopedLines = lines.filter((l) => l.AreaCode === row.areaCode && (!isLineScope || l.LineNumber === row.lineNumber));
    if (scopedLines.length === 0) return;

    const withTechDesc = scopedLines.filter((l) => !!l.TechnicalDescription || !!l.TechnicalDescriptionNL || !!l.TechnicalDescriptionFR || parseTechDescImages(l.TechDescImages).length > 0);
    let message = `Remove ${isLineScope ? 'this line group' : 'this area'}?\n\nScope: ${row.label}\nItems to remove: ${scopedLines.length}`;
    if (withTechDesc.length > 0) message += `\n\nThis will also remove technical descriptions on ${withTechDesc.length} item(s).`;
    message += '\n\nContinue?';
    if (!window.confirm(message)) return;

    const endpoint = isLineScope ? `/api/project-quotations/${quotationId}/lines/area/${row.areaCode}/line/${row.lineNumber}` : `/api/project-quotations/${quotationId}/lines/area/${row.areaCode}`;
    const res = await fetch(endpoint, { method: 'DELETE' });
    if (res.ok && onLineRemove) {
      scopedLines.forEach((l) => onLineRemove(l.ID));
      if (onConfigsRefresh) onConfigsRefresh();
    } else {
      onRefresh();
    }
  };

  const toggleBomExpand = async (line) => {
    const lineId = line.ID;
    const productId = line.ProductID;
    const newExpanded = new Set(expandedBomLines);

    if (newExpanded.has(lineId)) {
      newExpanded.delete(lineId);
      setExpandedBomLines(newExpanded);
      return;
    }

    // Fetch BOM tree if not cached
    if (!bomTrees[productId]) {
      setBomLoading((prev) => new Set(prev).add(lineId));
      try {
        const res = await fetch(`/api/products/${productId}/bom/tree`);
        const data = await res.json();
        setBomTrees((prev) => ({ ...prev, [productId]: data }));
      } catch (err) {
        console.log('Error fetching BOM tree:', err);
      }
      setBomLoading((prev) => {
        const next = new Set(prev);
        next.delete(lineId);
        return next;
      });
    }

    newExpanded.add(lineId);
    setExpandedBomLines(newExpanded);
  };

  const getCustomLabel = (areaCode, lineNumber, unitNumber) => {
    if (!Array.isArray(areaConfigs)) return null;
    const customLabel =
      areaConfigs.find(
        (c) => c.AreaCode === areaCode && (lineNumber == null ? c.LineNumber == null : c.LineNumber === lineNumber) && (unitNumber == null ? c.UnitNumber == null : c.UnitNumber === unitNumber)
      )?.CustomLabel || null;
    const normalizedLabel = normalizeResolvedLabel(customLabel);
    if (lineNumber != null && normalizedLabel) {
      const matchesProductDescription = lines.some(
        (item) =>
          item.AreaCode === areaCode &&
          item.LineNumber === lineNumber &&
          (unitNumber == null || (item.UnitNumber || 0) === unitNumber) &&
          normalizeResolvedLabel(getLineDescription(item)) === normalizedLabel
      );
      if (matchesProductDescription) return null;
    }
    return customLabel;
  };

  // Area-level configs may carry a per-quotation SortOrder override (same as the tree)
  const getAreaSortOverride = (areaCode) => {
    const config = (areaConfigs || []).find((c) => c.AreaCode === areaCode && c.LineNumber == null && c.UnitNumber == null);
    const parsed = Number.parseInt(config?.SortOrder, 10);
    return Number.isInteger(parsed) ? parsed : null;
  };
  const areaOrder = (areaCode) => getAreaSortOverride(areaCode) ?? AREAS[areaCode]?.order ?? 99;

  const sorted = [...lines].sort((a, b) => {
    const orderDiff = areaOrder(a.AreaCode) - areaOrder(b.AreaCode);
    if (orderDiff !== 0) return orderDiff;
    if (a.AreaCode !== b.AreaCode) return String(a.AreaCode).localeCompare(String(b.AreaCode));
    if ((a.LineNumber || 0) !== (b.LineNumber || 0)) return (a.LineNumber || 0) - (b.LineNumber || 0);
    if ((a.UnitNumber || 0) !== (b.UnitNumber || 0)) return (a.UnitNumber || 0) - (b.UnitNumber || 0);
    return (a.SortOrder || 0) - (b.SortOrder || 0) || a.ID - b.ID;
  });

  const getLineFinancials = (line) => {
    const discount = (line.DiscountPercent || 0) / 100;
    const total = (line.UnitSellingPrice || 0) * (line.Quantity || 0) * (1 - discount);
    const cost = (line.UnitPurchasePrice || 0) * (line.Quantity || 0) * (1 - discount);
    return { total, cost };
  };

  // Aggregate totals per area / line / unit for the group header rows
  const groupStats = new Map();
  const lineUnitSets = new Map();
  for (const line of sorted) {
    const unitNum = line.UnitNumber || 0;
    const { total, cost } = getLineFinancials(line);
    for (const key of [`A|${line.AreaCode}`, `L|${line.AreaCode}|${line.LineNumber}`, `U|${line.AreaCode}|${line.LineNumber}|${unitNum}`]) {
      const s = groupStats.get(key) || { count: 0, total: 0, cost: 0 };
      s.count += 1;
      s.total += total;
      s.cost += cost;
      groupStats.set(key, s);
    }
    const lineKey = `L|${line.AreaCode}|${line.LineNumber}`;
    if (!lineUnitSets.has(lineKey)) lineUnitSets.set(lineKey, new Set());
    lineUnitSets.get(lineKey).add(unitNum);
  }

  // Build display rows: group headers (area > line > unit) followed by their items
  const rows = [];
  const itemIds = [];
  const visibleAreas = [];
  let prevArea = null;
  let prevLine = null;
  let prevUnit = null;
  for (const line of sorted) {
    const unitNum = line.UnitNumber || 0;
    const lineKey = `L|${line.AreaCode}|${line.LineNumber}`;
    const unitKey = `U|${line.AreaCode}|${line.LineNumber}|${unitNum}`;

    if (line.AreaCode !== prevArea) {
      rows.push({
        type: 'group',
        level: 'area',
        key: `A|${line.AreaCode}`,
        areaCode: line.AreaCode,
        label: getCustomLabel(line.AreaCode, null, null) || AREAS[line.AreaCode]?.label || line.AreaCode,
        stats: groupStats.get(`A|${line.AreaCode}`)
      });
      visibleAreas.push(line.AreaCode);
      prevArea = line.AreaCode;
      prevLine = null;
      prevUnit = null;
    }

    if (line.LineNumber !== prevLine) {
      rows.push({
        type: 'group',
        level: 'line',
        key: lineKey,
        areaCode: line.AreaCode,
        lineNumber: line.LineNumber,
        code: String(line.LineNumber).padStart(3, '0'),
        label: getCustomLabel(line.AreaCode, line.LineNumber, null) || getLineLabel(line.LineNumber),
        stats: groupStats.get(lineKey)
      });
      prevLine = line.LineNumber;
      prevUnit = null;
    }

    const unitStats = groupStats.get(unitKey);
    if (unitNum !== prevUnit) {
      // Solo units skip the header: the item row itself carries the description
      if (unitStats.count > 1) {
        rows.push({
          type: 'group',
          level: 'unit',
          key: unitKey,
          areaCode: line.AreaCode,
          lineNumber: line.LineNumber,
          unitNumber: unitNum,
          code: String(unitNum).padStart(4, '0'),
          label: getCustomLabel(line.AreaCode, line.LineNumber, unitNum) || getUnitLabel(unitNum),
          stats: unitStats
        });
      }
      prevUnit = unitNum;
    }

    rows.push({ type: 'item', key: `item-${line.ID}`, line, flatIndex: itemIds.length });
    itemIds.push(line.ID);

    if (line.ShowBomBreakdown && line.Product?.HasDetails && expandedBomLines.has(line.ID) && bomTrees[line.ProductID]) {
      const flatBom = flattenBomTree(bomTrees[line.ProductID].tree, line.Quantity || 1, 1);
      flatBom.forEach((bomRow, bomIndex) => {
        rows.push({ type: 'bom', key: `bom-${line.ID}-${bomIndex}`, bomRow });
      });
    }
  }

  // Column-wise keyboard navigation between item rows (Enter / arrow keys)
  const focusCell = (lineId, col) => {
    const el = wrapRef.current?.querySelector(`[data-cell="${lineId}:${col}"]`);
    if (!el) return false;
    el.focus();
    return true;
  };

  const navigateVertical = (lineId, col, direction) => {
    const idx = itemIds.indexOf(lineId);
    if (idx < 0) return false;
    const nextId = itemIds[idx + direction];
    if (nextId == null) return false;
    return focusCell(nextId, col);
  };

  const expandedLines = lines.filter((l) => expandedTechDesc.has(l.ID));
  const headings = ['Ref', 'Description', 'Unit', 'Qty', 'AP', 'VP', 'Disc.', 'Total', 'Margin', ''];
  const numericColumns = new Set([2, 3, 4, 5, 6, 7, 8]);

  const renderGroupRow = (row) => {
    const stats = row.stats || { count: 0, total: 0, cost: 0 };
    const marginPct = stats.total > 0 ? ((stats.total - stats.cost) / stats.total) * 100 : 0;
    const areaIdx = row.level === 'area' ? visibleAreas.indexOf(row.areaCode) : -1;

    return (
      <tr key={row.key} className={`qlt-group qlt-group-${row.level}`}>
        <td colSpan={7}>
          <div className="qlt-group-cell">
            {row.code != null && <span className="qlt-group-code">{row.code}</span>}
            {isLocked ? (
              <span className="qlt-group-label" title={row.label}>
                {row.label}
              </span>
            ) : (
              <CellInput align="left" display={row.label} editValue={row.label} title={`${row.label} — click to rename`} cellKey={`group-${row.key}`} onCommit={(raw) => saveGroupLabel(row, raw)} />
            )}
            <span className="qlt-group-count">
              {stats.count} item{stats.count === 1 ? '' : 's'}
            </span>
          </div>
        </td>
        <td className="numeric qlt-group-total">{formatToCurrency(stats.total)}</td>
        <td className="numeric qlt-group-margin">
          <span style={{ color: marginColorFor(marginPct) }}>{marginPct.toFixed(1)}%</span>
        </td>
        <td>
          {!isLocked && (
            <div className="qlt-actions">
              {row.level === 'area' && (
                <>
                  <button type="button" className="qlt-icon-btn" title="Move area up" disabled={areaIdx <= 0} onClick={() => moveArea(row.areaCode, -1, visibleAreas)}>
                    ↑
                  </button>
                  <button type="button" className="qlt-icon-btn" title="Move area down" disabled={areaIdx === visibleAreas.length - 1} onClick={() => moveArea(row.areaCode, 1, visibleAreas)}>
                    ↓
                  </button>
                </>
              )}
              <button type="button" className="qlt-icon-btn" title={`Add product to ${row.label}`} onClick={() => onAddToGroup?.(row.areaCode, row.lineNumber ?? null, row.unitNumber ?? null)}>
                +
              </button>
              {row.level !== 'unit' && (
                <button
                  type="button"
                  className="qlt-icon-btn qlt-icon-btn-danger"
                  title={`Remove ${row.level === 'area' ? 'area' : 'line group'} and all its items`}
                  onClick={() => handleRemoveScope(row)}
                >
                  ✕
                </button>
              )}
            </div>
          )}
        </td>
      </tr>
    );
  };

  const renderItemRow = (row, position) => {
    const { line } = row;
    const { total: lineTotal, cost: lineCost } = getLineFinancials(line);
    const marginPct = lineTotal > 0 ? ((lineTotal - lineCost) / lineTotal) * 100 : 0;
    const canExpandBom = line.ShowBomBreakdown && line.Product?.HasDetails;
    const isExpanded = expandedBomLines.has(line.ID);
    const isLoadingBom = bomLoading.has(line.ID);

    const nav = (col) => (direction) => navigateVertical(line.ID, col, direction);
    const move = (direction) => moveLine(line, direction);
    const cell = (col, props) => (
      <CellInput key={col} cellKey={`${line.ID}:${col}`} disabled={isLocked} onCommit={(raw) => commitField(line, col, raw)} onNavigate={nav(col)} onMoveLine={move} {...props} />
    );

    return (
      <tr key={row.key} className="qlt-item-row">
        <td>
          {isLocked ? (
            <Text variant="bodySm" as="span" tone="subdued">
              {line.Product?.Productref || ''}
            </Text>
          ) : (
            <button type="button" className="qlt-ref-btn" title={line.ProductID ? 'Replace the product on this line' : 'Link a product to this line'} onClick={() => onReplaceProduct?.(line)}>
              {line.Product?.Productref || (line.ProductID ? `#${line.ProductID}` : '+ product')}
            </button>
          )}
        </td>
        <td>
          <div className="qlt-desc-cell">
            {canExpandBom && (
              <span onClick={() => toggleBomExpand(line)} style={{ cursor: 'pointer', display: 'inline-flex', userSelect: 'none', paddingTop: '3px' }}>
                {isLoadingBom ? <Spinner size="small" /> : isExpanded ? '▼' : '▶'}
              </span>
            )}
            {canExpandBom && (
              <Badge size="small" tone="info">
                BOM
              </Badge>
            )}
            {cell('desc', {
              align: 'left',
              display: line.Description || line.Product?.ProductNameNL || '',
              editValue: line.Description || '',
              title: line.Description || line.Product?.ProductNameNL || '',
              inputStyle: line.IsOptional ? { fontStyle: 'italic', color: '#888' } : undefined
            })}
          </div>
        </td>
        <td className="numeric">{cell('unit', { display: String(line.UnitNumber || 0), editValue: line.UnitNumber || 0, title: getUnitLabel(line.UnitNumber || 0) })}</td>
        <td className="numeric">{cell('qty', { display: String(line.Quantity ?? ''), editValue: parseFloat(line.Quantity) || 0 })}</td>
        <td className="numeric">{cell('ap', { display: formatToCurrency(line.UnitPurchasePrice || 0), editValue: parseFloat(line.UnitPurchasePrice) || 0 })}</td>
        <td className="numeric">{cell('vp', { display: formatToCurrency(line.UnitSellingPrice || 0), editValue: parseFloat(line.UnitSellingPrice) || 0 })}</td>
        <td className="numeric">{cell('disc', { display: line.DiscountPercent ? `${line.DiscountPercent}%` : '', editValue: parseFloat(line.DiscountPercent) || 0, placeholder: '0%' })}</td>
        <td className="numeric">{cell('total', { display: formatToCurrency(lineTotal), editValue: roundTwo(lineTotal) })}</td>
        <td className="numeric">
          {cell('margin', {
            display: `${marginPct.toFixed(1)}%`,
            editValue: marginPct.toFixed(1),
            inputStyle: { color: marginColorFor(marginPct), fontWeight: 600 },
            title: 'Type a target margin % to recompute VP'
          })}
        </td>
        <td>
          {!isLocked && (
            <div className="qlt-actions">
              <button type="button" className="qlt-icon-btn" title="Move up (Alt+↑)" disabled={position.isFirst} onClick={() => moveLine(line, -1)}>
                ↑
              </button>
              <button type="button" className="qlt-icon-btn" title="Move down (Alt+↓)" disabled={position.isLast} onClick={() => moveLine(line, 1)}>
                ↓
              </button>
              <button type="button" className="qlt-icon-btn" title="Duplicate line" onClick={() => handleDuplicate(line)}>
                ⧉
              </button>
              <button type="button" className={`qlt-icon-btn${expandedTechDesc.has(line.ID) ? ' qlt-icon-btn-active' : ''}`} title="Technical description" onClick={() => toggleTechDesc(line.ID)}>
                TD
              </button>
              <button
                type="button"
                className={`qlt-icon-btn${line.IsOptional ? ' qlt-icon-btn-active' : ''}`}
                title={line.IsOptional ? 'Optional — click to make standard' : 'Standard — click to make optional'}
                onClick={() => saveLine(line, { IsOptional: !line.IsOptional })}
              >
                Opt
              </button>
              <button type="button" className="qlt-icon-btn qlt-icon-btn-danger" title="Remove line" onClick={() => handleDelete(line)}>
                ✕
              </button>
            </div>
          )}
        </td>
      </tr>
    );
  };

  const renderBomRow = (row) => {
    const { bomRow } = row;
    const indent = bomRow.level * 16;
    const bomLineCost = bomRow.isLeaf ? bomRow.unitCost * bomRow.quantity : 0;

    return (
      <tr key={row.key} className="qlt-bom-row">
        <td>
          <Text variant="bodySm" as="span" tone="subdued">
            {bomRow.ref || ''}
          </Text>
        </td>
        <td>
          <div style={{ paddingLeft: `${indent}px`, maxWidth: '100%' }}>
            <span style={{ color: '#666', fontSize: '12px' }} title={bomRow.name}>
              {bomRow.isLeaf ? '└ ' : '├ '}
              {bomRow.name}{' '}
              {!bomRow.isLeaf && (
                <Badge size="small" tone="attention">
                  Assembly
                </Badge>
              )}
            </span>
          </div>
        </td>
        <td />
        <td className="numeric">
          <Text variant="bodySm" as="span" tone="subdued">
            {bomRow.quantity}
          </Text>
        </td>
        <td className="numeric">
          <Text variant="bodySm" as="span" tone="subdued">
            {!bomRow.isLeaf ? <span style={{ textDecoration: 'line-through', opacity: 0.5 }}>{formatToCurrency(bomRow.unitCost)}</span> : formatToCurrency(bomRow.unitCost)}
          </Text>
        </td>
        <td />
        <td />
        <td className="numeric">
          <Text variant="bodySm" as="span" tone="subdued">
            {bomRow.isLeaf ? formatToCurrency(bomLineCost) : ''}
          </Text>
        </td>
        <td />
        <td />
      </tr>
    );
  };

  return (
    <>
      <style>{`
        .quotation-line-table-wrap {
          overflow-x: auto;
          border: 1px solid #e1e3e5;
          border-radius: 8px;
          background: #fff;
        }

        .quotation-line-table {
          table-layout: fixed;
          width: 100%;
          min-width: 1150px;
          border-collapse: collapse;
        }

        .quotation-line-table th,
        .quotation-line-table td {
          vertical-align: middle;
          padding: 3px 6px;
          border-bottom: 1px solid #f1f2f3;
          font-size: 13px;
          text-align: left;
        }

        .quotation-line-table thead th {
          position: sticky;
          top: 0;
          z-index: 1;
          background: #f6f6f7;
          color: #616161;
          font-size: 12px;
          font-weight: 600;
          padding: 8px 6px;
        }

        .quotation-line-table th:nth-child(1),
        .quotation-line-table td:nth-child(1) {
          width: 80px;
          white-space: normal;
          word-break: break-word;
        }

        /* Description (2nd col) takes the remaining width */

        .quotation-line-table th:nth-child(3),
        .quotation-line-table td:nth-child(3) {
          width: 58px;
        }

        .quotation-line-table th:nth-child(4),
        .quotation-line-table td:nth-child(4) {
          width: 72px;
        }

        .quotation-line-table th:nth-child(5),
        .quotation-line-table td:nth-child(5),
        .quotation-line-table th:nth-child(6),
        .quotation-line-table td:nth-child(6) {
          width: 115px;
        }

        .quotation-line-table th:nth-child(7),
        .quotation-line-table td:nth-child(7) {
          width: 66px;
        }

        .quotation-line-table th:nth-child(8),
        .quotation-line-table td:nth-child(8) {
          width: 122px;
        }

        .quotation-line-table th:nth-child(9),
        .quotation-line-table td:nth-child(9) {
          width: 80px;
        }

        .quotation-line-table th:nth-child(10),
        .quotation-line-table td:nth-child(10) {
          width: 168px;
        }

        .quotation-line-table td.numeric,
        .quotation-line-table th.numeric {
          text-align: right;
          white-space: nowrap;
        }

        .qlt-cell-input {
          width: 100%;
          box-sizing: border-box;
          border: 1px solid transparent;
          border-radius: 4px;
          background: transparent;
          font: inherit;
          font-size: 13px;
          color: inherit;
          padding: 3px 5px;
          margin: 0;
        }

        .qlt-cell-input-numeric {
          text-align: right;
          font-variant-numeric: tabular-nums;
        }

        .qlt-item-row:hover .qlt-cell-input {
          border-color: #e1e3e5;
          background: #fff;
        }

        .qlt-cell-input:focus {
          outline: none;
          border-color: #005bd3;
          background: #fff;
          box-shadow: 0 0 0 2px rgba(0, 91, 211, 0.15);
        }

        .qlt-cell-static {
          display: block;
          padding: 3px 5px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .qlt-cell-static-numeric {
          text-align: right;
          font-variant-numeric: tabular-nums;
        }

        .qlt-desc-cell {
          display: flex;
          align-items: center;
          gap: 6px;
          min-width: 0;
        }

        .qlt-desc-cell .qlt-cell-input,
        .qlt-desc-cell .qlt-cell-static {
          flex: 1;
          min-width: 0;
        }

        .qlt-ref-btn {
          border: none;
          background: transparent;
          padding: 3px 0;
          font-size: 12px;
          color: #6d7175;
          cursor: pointer;
          text-align: left;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .qlt-ref-btn:hover {
          color: #005bd3;
          text-decoration: underline dashed;
          text-underline-offset: 3px;
        }

        .qlt-actions {
          display: flex;
          gap: 2px;
          align-items: center;
        }

        .qlt-icon-btn {
          border: 1px solid transparent;
          background: transparent;
          border-radius: 4px;
          font-size: 11px;
          line-height: 1.5;
          color: #616161;
          cursor: pointer;
          padding: 2px 5px;
        }

        .qlt-icon-btn:hover:not(:disabled) {
          background: #f1f2f3;
          border-color: #d1d3d5;
          color: #202223;
        }

        .qlt-icon-btn:disabled {
          opacity: 0.3;
          cursor: default;
        }

        .qlt-icon-btn-active {
          background: #e5effd;
          border-color: #005bd3;
          color: #005bd3;
        }

        .qlt-icon-btn-danger:hover:not(:disabled) {
          background: #fdeeee;
          border-color: #e74c3c;
          color: #c0392b;
        }

        .quotation-line-table .qlt-group td {
          border-bottom: 1px solid #e7e8ea;
          padding-top: 6px;
          padding-bottom: 6px;
        }

        .quotation-line-table .qlt-group-cell {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }

        .quotation-line-table .qlt-group-cell .qlt-cell-input {
          flex: 1;
          min-width: 0;
          font-weight: 600;
        }

        .quotation-line-table .qlt-group-unit .qlt-group-cell .qlt-cell-input {
          font-weight: 400;
          color: #6d7175;
        }

        .quotation-line-table .qlt-group:hover .qlt-cell-input {
          border-color: #e1e3e5;
          background: #fff;
        }

        .quotation-line-table .qlt-group-label {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .quotation-line-table .qlt-group-code {
          color: #8c9196;
          font-weight: 500;
          margin-right: 6px;
          font-variant-numeric: tabular-nums;
        }

        .quotation-line-table .qlt-group-count {
          flex-shrink: 0;
          color: #8c9196;
          font-size: 12px;
          font-weight: 400;
        }

        .quotation-line-table .qlt-group-margin {
          font-size: 12px;
        }

        .quotation-line-table .qlt-group-area td {
          background: #f1f2f4;
        }

        .quotation-line-table .qlt-group-area .qlt-group-label {
          font-weight: 600;
        }

        .quotation-line-table .qlt-group-area .qlt-group-total {
          font-weight: 600;
        }

        .quotation-line-table .qlt-group-line td {
          background: #fafbfb;
        }

        .quotation-line-table .qlt-group-line .qlt-group-cell {
          padding-left: 26px;
        }

        .quotation-line-table .qlt-group-line .qlt-group-label {
          font-weight: 600;
        }

        .quotation-line-table .qlt-group-unit td {
          background: #fcfcfd;
        }

        .quotation-line-table .qlt-group-unit .qlt-group-cell {
          padding-left: 44px;
        }

        .quotation-line-table .qlt-group-unit .qlt-group-label {
          color: #6d7175;
        }

        .quotation-line-table .qlt-group-unit .qlt-group-total,
        .quotation-line-table .qlt-group-line .qlt-group-total {
          color: #6d7175;
        }

        .quotation-line-table .qlt-bom-row td {
          background: #fbfbfc;
        }

        .qlt-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          padding: 4px 2px 0;
        }

        .qlt-hint {
          color: #8c9196;
          font-size: 11px;
        }

        .qlt-save-state {
          flex-shrink: 0;
          color: #8c9196;
          font-size: 11px;
        }

        .qlt-save-state.saving {
          color: #005bd3;
          font-weight: 600;
        }
      `}</style>
      <div className="quotation-line-table-wrap" ref={wrapRef}>
        <table className="quotation-line-table">
          <thead>
            <tr>
              {headings.map((heading, index) => (
                <th key={`${heading}-${index}`} className={numericColumns.has(index) ? 'numeric' : ''}>
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMN_COUNT}>
                  <Text variant="bodySm" as="span" tone="subdued">
                    No items in this selection.
                  </Text>
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                if (row.type === 'group') return renderGroupRow(row);
                if (row.type === 'bom') return renderBomRow(row);
                return renderItemRow(row, { isFirst: row.flatIndex === 0, isLast: row.flatIndex === itemIds.length - 1 });
              })
            )}
          </tbody>
        </table>
      </div>
      {!isLocked && (
        <div className="qlt-footer">
          <span className="qlt-hint">
            Click any cell (or group title) to edit — saved on leaving the cell · Enter / ↑ ↓ move between rows · Tab moves across · Alt+↑↓ moves the line · Esc cancels · Click a Ref to swap the
            product
          </span>
          <span className={`qlt-save-state${pendingSaves > 0 ? ' saving' : ''}`}>{pendingSaves > 0 ? 'Saving…' : hasSaved ? 'All changes saved' : ''}</span>
        </div>
      )}
      {expandedLines.map((line) => (
        <LineTechDescEditor key={line.ID} line={line} quotationId={quotationId} onRefresh={onRefresh} disabled={isLocked} />
      ))}
    </>
  );
}
